import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError, fmtDateTime } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Download, Printer, Check, AlertTriangle, GraduationCap } from "lucide-react";

const ROLES = ["admin", "pathologist", "supervisor", "tech", "viewer"];

export default function TrainingMatrix() {
  const [meta, setMeta] = useState(null);
  const [data, setData] = useState(null);
  const [filters, setFilters] = useState({ doc_type: "", department: "", role: "" });

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get("/documents/matrix", { params: filters });
      setData(d);
    } catch (e) { toast.error(apiError(e)); }
  }, [filters]);

  useEffect(() => { api.get("/documents/meta").then((r) => setMeta(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const exportCsv = async () => {
    try {
      const res = await api.get("/documents/matrix.csv", { params: filters, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `training-matrix-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Matrix exported");
    } catch (e) { toast.error(apiError(e)); }
  };

  const tone = (p) => (p === 100 ? "text-green-800" : p >= 60 ? "text-amber-700" : "text-red-800");

  return (
    <div className="space-y-6" data-testid="training-matrix-page">
      <div className="no-print">
        <Link to="/documents" className="inline-flex items-center gap-2 text-sm font-semibold text-[#134e4a] hover:underline" data-testid="matrix-back">
          <ArrowLeft size={15} /> All documents
        </Link>
      </div>

      <div className="flex flex-wrap items-end gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#134e4a]">PolicyTraX</p>
          <h1 className="mt-1 flex items-center gap-2 font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            <GraduationCap size={26} className="text-[#134e4a]" /> Training matrix
          </h1>
          {data && (
            <p className="mt-1 text-sm text-slate-600" data-testid="matrix-headline">
              {data.document_count} documents × {data.staff_count} staff ·{" "}
              <span className={`font-semibold ${tone(data.percent)}`}>{data.percent}% signed</span> ·{" "}
              <span className={data.gaps ? "font-semibold text-red-800" : "text-slate-600"}>{data.gaps} gaps</span>
              <span className="ml-2 text-xs text-slate-500">as of {fmtDateTime(data.generated_at)}</span>
            </p>
          )}
        </div>
        <div className="no-print ml-auto flex flex-wrap gap-2">
          <Button variant="outline" onClick={exportCsv} data-testid="matrix-export-csv" className="h-11 border-slate-300">
            <Download size={15} className="mr-2" /> Export CSV
          </Button>
          <Button variant="outline" onClick={() => window.print()} data-testid="matrix-print" className="h-11 border-slate-300">
            <Printer size={15} className="mr-2" /> Print
          </Button>
        </div>
      </div>

      <div className="no-print flex flex-wrap items-end gap-3 border border-slate-200 bg-slate-50 p-3">
        <div>
          <Label className="text-xs uppercase tracking-wide text-slate-600">Type</Label>
          <select value={filters.doc_type} onChange={(e) => setFilters((f) => ({ ...f, doc_type: e.target.value }))}
                  data-testid="matrix-type-filter" className="mt-1 h-11 border border-slate-300 bg-white px-3 text-sm">
            <option value="">All types</option>
            {(meta?.doc_types || []).map((t) => <option key={t.code} value={t.code}>{t.label}</option>)}
          </select>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wide text-slate-600">Department</Label>
          <select value={filters.department} onChange={(e) => setFilters((f) => ({ ...f, department: e.target.value }))}
                  data-testid="matrix-department-filter" className="mt-1 h-11 border border-slate-300 bg-white px-3 text-sm">
            <option value="">All departments</option>
            {(meta?.departments || []).map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wide text-slate-600">Role</Label>
          <select value={filters.role} onChange={(e) => setFilters((f) => ({ ...f, role: e.target.value }))}
                  data-testid="matrix-role-filter" className="mt-1 h-11 border border-slate-300 bg-white px-3 text-sm">
            <option value="">All roles</option>
            {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
        <Button variant="outline" onClick={() => setFilters({ doc_type: "", department: "", role: "" })}
                data-testid="matrix-clear-filters" className="h-11 border-slate-300">Clear</Button>
      </div>

      {!data ? (
        <p className="text-sm text-slate-500" data-testid="matrix-loading">Building the matrix…</p>
      ) : data.document_count === 0 ? (
        <p className="border border-dashed border-slate-300 px-3 py-10 text-center text-sm text-slate-500" data-testid="matrix-empty">
          No effective documents require a signature yet. Approve an SOP and it will appear here.
        </p>
      ) : (
        <div className="overflow-x-auto border border-slate-200">
          <table className="w-full text-sm" data-testid="matrix-table">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                <th className="sticky left-0 z-10 bg-slate-50 px-3 py-2 text-left text-xs uppercase tracking-wide text-slate-500">Staff</th>
                {data.documents.map((d) => (
                  <th key={d.id} className="px-2 py-2 text-center text-[11px] font-semibold text-slate-600" title={`${d.title} (${d.department})`}>
                    <Link to={`/documents/${d.id}`} className="font-mono hover:underline" data-testid={`matrix-col-${d.control_number}`}>
                      {d.control_number}
                    </Link>
                    <div className="num font-normal text-slate-400">Rev {d.version}</div>
                  </th>
                ))}
                <th className="px-3 py-2 text-right text-xs uppercase tracking-wide text-slate-500">Complete</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r) => (
                <tr key={r.user_id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`matrix-row-${r.user_id}`}>
                  <td className="sticky left-0 z-10 bg-white px-3 py-2">
                    <div className="font-medium text-slate-900">{r.name}</div>
                    <div className="text-[11px] uppercase text-slate-500">{r.role}</div>
                  </td>
                  {r.cells.map((c) => (
                    <td key={c.document_id} className="px-2 py-2 text-center"
                        title={c.signed ? `Signed ${fmtDateTime(c.at)}` : "Outstanding"}
                        data-testid={`matrix-cell-${r.user_id}-${c.document_id}`}>
                      {c.signed
                        ? <Check size={16} className="mx-auto text-green-800" />
                        : <AlertTriangle size={15} className="mx-auto text-amber-600" />}
                    </td>
                  ))}
                  <td className={`num px-3 py-2 text-right font-semibold ${tone(r.percent)}`} data-testid={`matrix-row-percent-${r.user_id}`}>
                    {r.percent}%
                  </td>
                </tr>
              ))}
              <tr className="bg-slate-50 font-semibold">
                <td className="sticky left-0 z-10 bg-slate-50 px-3 py-2 text-xs uppercase tracking-wide text-slate-500">Complete</td>
                {data.documents.map((d) => (
                  <td key={d.id} className={`num px-2 py-2 text-center text-xs ${tone(d.percent)}`} data-testid={`matrix-col-percent-${d.id}`}>
                    {d.percent}%
                  </td>
                ))}
                <td className={`num px-3 py-2 text-right ${tone(data.percent)}`} data-testid="matrix-total-percent">{data.percent}%</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      <p className="text-xs text-slate-500">
        Green tick = read-and-understood signed for the current revision (hover for the date). Amber = outstanding.
        Signatures reset whenever a new revision becomes effective.
      </p>
    </div>
  );
}
